import { Box, Button, Heading, Page } from '@zillow/constellation-10';
import { IconPlusCircleFilled } from '@zillow/constellation-icons';

import { useScreenSize } from '../../../hooks/useScreenSize';
import accessUtils from '../../../utils/accessUtils';

import { EVENT_IDS } from '../../../analytics/events/addPropertyModal';
import { useAuth } from '../../../components/AuthProvider';

import type { ButtonPropsInterface, HeadingPropsInterface } from '@zillow/constellation-10';

type Props = {
  handleAddPropertyClick: () => void;
  handleAddFeedListingsClick?: () => void;
  headingTextStyle?: HeadingPropsInterface['textStyle'];
  buttonSize?: ButtonPropsInterface['size'];
  buttonEmphasis?: ButtonPropsInterface['emphasis'];
  buttonIconPosition?: ButtonPropsInterface['iconPosition'];
  buttonIcon?: ButtonPropsInterface['icon'];
};

export const PageHeading = ({
  handleAddPropertyClick,
  handleAddFeedListingsClick,
  headingTextStyle = 'heading-md',
  buttonSize = 'md',
  buttonEmphasis = 'outlined',
  buttonIconPosition = 'end',
  buttonIcon = <IconPlusCircleFilled />,
}: Props) => {
  const { user } = useAuth();
  const { isScreenMobile } = useScreenSize();

  const showAddPropertyButton = user?.features?.canAddListing ?? false;
  const canAccessAddFeedListingsModal = accessUtils.canAccessAddFeedListingsModal({ user });
  const showAddFeedButton = Boolean(handleAddFeedListingsClick) && canAccessAddFeedListingsModal;
  const showButtonsNextRow = isScreenMobile && showAddPropertyButton && showAddFeedButton;

  const pageTitle = 'Properties';

  const renderAddButtons = () => (
    <Box
      css={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        gap: '600',
      }}
    >
      {showAddPropertyButton && (
        <Button
          tone="brand"
          emphasis={buttonEmphasis}
          onClick={handleAddPropertyClick}
          data-testid="AddPropertyButton"
          data-eventid={EVENT_IDS.OPEN}
          data-eventdescription="View add property modal"
          icon={buttonIcon}
          iconPosition={buttonIconPosition}
          size={buttonSize}
        >
          Add property
        </Button>
      )}
      {showAddFeedButton && (
        <Button
          tone="brand"
          emphasis={buttonEmphasis}
          onClick={handleAddFeedListingsClick}
          data-eventid={EVENT_IDS.ADD_FEED_CLICK}
          data-eventdescription="View add feed modal"
          icon={buttonIcon}
          iconPosition={buttonIconPosition}
          size={buttonSize}
        >
          Add feed
        </Button>
      )}
    </Box>
  );

  return (
    <Page.Content css={{ marginBottom: '400' }}>
      {!showButtonsNextRow && (
        <Box css={{ display: 'flex', alignItems: 'center' }}>
          <Box css={{ flex: '1' }}>
            <Heading level={1} textStyle={headingTextStyle}>
              {pageTitle}
            </Heading>
          </Box>
          <Box css={{ paddingLeft: '400' }}>{renderAddButtons()}</Box>
        </Box>
      )}
      {showButtonsNextRow && (
        <Box>
          <Heading level={1} textStyle={headingTextStyle}>
            {pageTitle}
          </Heading>
          <Box css={{ paddingTop: '400' }}>{renderAddButtons()}</Box>
        </Box>
      )}
    </Page.Content>
  );
};
